import React from 'react';
import { uploadFileAsset } from '../../../../../helpers/file-helper';
import {
    bulkStatusFollowUpdate,
    createTicketFollow,
    updateTicket,
} from '../../../../../helpers/ticket-helper';
import { textToHtml } from '../helpers';

export const useTaskListStatusModal = ({
    token,
    tickets,
    selectedTicketIds,
    setSelectedTicketIds,
    loadTickets,
    loadDashboardStats,
}) => {
    const [isStatusModalOpen, setIsStatusModalOpen] = React.useState(false);
    const [statusModalMode, setStatusModalMode] = React.useState('single');
    const [statusModalTicketId, setStatusModalTicketId] = React.useState('');
    const [statusModalTargetStatus, setStatusModalTargetStatus] = React.useState('');
    const [statusModalNote, setStatusModalNote] = React.useState('');
    const [statusModalIsPublic, setStatusModalIsPublic] = React.useState(true);
    const [statusModalAttachments, setStatusModalAttachments] = React.useState([]);
    const [statusModalError, setStatusModalError] = React.useState('');
    const [isSavingStatusModal, setIsSavingStatusModal] = React.useState(false);

    const openStatusModalForSingle = React.useCallback((ticketId, nextStatus) => {
        setStatusModalMode('single');
        setStatusModalTicketId(String(ticketId || ''));
        setStatusModalTargetStatus(nextStatus || '');
        setStatusModalNote('');
        setStatusModalIsPublic(true);
        setStatusModalAttachments([]);
        setStatusModalError('');
        setIsStatusModalOpen(true);
    }, []);

    const openStatusModalForBulk = React.useCallback(() => {
        if (selectedTicketIds.length === 0) {
            return {
                success: false,
                message: 'Selecciona al menos un ticket para aplicar cambios masivos.',
            };
        }

        setStatusModalMode('bulk');
        setStatusModalTicketId('');
        setStatusModalTargetStatus('');
        setStatusModalNote('');
        setStatusModalIsPublic(true);
        setStatusModalAttachments([]);
        setStatusModalError('');
        setIsStatusModalOpen(true);

        return { success: true };
    }, [selectedTicketIds.length]);

    const closeStatusModal = React.useCallback(() => {
        if (isSavingStatusModal) return;

        setIsStatusModalOpen(false);
        setStatusModalError('');
    }, [isSavingStatusModal]);

    const handleStatusChangeIntent = React.useCallback((ticket, nextStatus) => {
        const currentStatus = String(ticket?.status || '');
        const normalized = String(nextStatus || '');

        if (!normalized || normalized === currentStatus) {
            return;
        }

        openStatusModalForSingle(ticket?._id, normalized);
    }, [openStatusModalForSingle]);

    const handleRemoveModalAttachment = React.useCallback((indexToRemove) => {
        setStatusModalAttachments((previous) => previous.filter((_, index) => index !== indexToRemove));
    }, []);

    const handleSubmitStatusModal = React.useCallback(async () => {
        if (!token || isSavingStatusModal) return;

        const noteHtml = textToHtml(statusModalNote);

        if (!statusModalTargetStatus) {
            setStatusModalError('Selecciona un estatus.');
            return;
        }

        if (!noteHtml) {
            setStatusModalError('La nota es obligatoria para registrar el cambio.');
            return;
        }

        setStatusModalError('');
        setIsSavingStatusModal(true);

        if (statusModalMode === 'single') {
            const updateResp = await updateTicket(token, statusModalTicketId, {
                status: statusModalTargetStatus,
            });

            if (!updateResp?.success) {
                setStatusModalError(updateResp?.message || 'No se pudo actualizar el estatus del ticket.');
                setIsSavingStatusModal(false);
                return;
            }

            const followResp = await createTicketFollow(token, statusModalTicketId, {
                contentHtml: noteHtml,
                isPublic: statusModalIsPublic,
            });

            if (!followResp?.success || !followResp?.data?._id) {
                setStatusModalError(followResp?.message || 'No se pudo crear el seguimiento.');
                setIsSavingStatusModal(false);
                return;
            }

            if (statusModalAttachments.length > 0) {
                await Promise.all(statusModalAttachments.map((file) => {
                    return uploadFileAsset(token, {
                        file,
                        purpose: 'TICKET_FOLLOW_ATTACHMENT',
                        tags: ['ticket-follow', statusModalTicketId],
                        referenceType: 'TICKET_FOLLOW',
                        referenceId: followResp.data._id,
                        referenceField: 'attachments',
                        referenceLabel: updateResp?.data?.code || statusModalTicketId,
                        isPublic: false,
                    });
                }));
            }
        } else {
            const bulkResp = await bulkStatusFollowUpdate(token, {
                ticketIds: selectedTicketIds,
                status: statusModalTargetStatus,
                contentHtml: noteHtml,
                isPublic: statusModalIsPublic,
            });

            if (!bulkResp?.success) {
                setStatusModalError(bulkResp?.message || 'No se pudo aplicar el cambio masivo.');
                setIsSavingStatusModal(false);
                return;
            }

            const updatedRows = Array.isArray(bulkResp?.data?.updatedTickets) ? bulkResp.data.updatedTickets : [];

            if (statusModalAttachments.length > 0 && updatedRows.length > 0) {
                await Promise.all(updatedRows.map((row) => {
                    if (!row.followId) {
                        return Promise.resolve();
                    }

                    return Promise.all(statusModalAttachments.map((file) => {
                        return uploadFileAsset(token, {
                            file,
                            purpose: 'TICKET_FOLLOW_ATTACHMENT',
                            tags: ['ticket-follow', row.ticketId],
                            referenceType: 'TICKET_FOLLOW',
                            referenceId: row.followId,
                            referenceField: 'attachments',
                            referenceLabel: row.ticketCode || row.ticketId,
                            isPublic: false,
                        });
                    }));
                }));
            }

            setSelectedTicketIds([]);
        }

        setIsSavingStatusModal(false);
        setIsStatusModalOpen(false);
        await Promise.all([loadTickets(), loadDashboardStats()]);
    }, [
        token,
        isSavingStatusModal,
        statusModalNote,
        statusModalTargetStatus,
        statusModalMode,
        statusModalTicketId,
        statusModalIsPublic,
        statusModalAttachments,
        selectedTicketIds,
        setSelectedTicketIds,
        loadTickets,
        loadDashboardStats,
    ]);

    const singleModalTicket = React.useMemo(() => {
        return tickets.find((ticket) => String(ticket._id) === String(statusModalTicketId));
    }, [tickets, statusModalTicketId]);

    const statusModalTitle = statusModalMode === 'bulk'
        ? `Cambio masivo (${selectedTicketIds.length})`
        : `Actualizar estatus ${singleModalTicket?.code || ''}`;

    const statusModalSubtitle = statusModalMode === 'bulk'
        ? 'Se actualizará el estatus y se agregará la misma nota a todos los tickets seleccionados.'
        : 'Este cambio actualizará el estatus del ticket y registrará un seguimiento.';

    return {
        isStatusModalOpen,
        statusModalMode,
        statusModalTargetStatus,
        statusModalNote,
        statusModalIsPublic,
        statusModalAttachments,
        statusModalError,
        isSavingStatusModal,
        statusModalTitle,
        statusModalSubtitle,
        openStatusModalForBulk,
        handleStatusChangeIntent,
        setStatusModalTargetStatus,
        setStatusModalNote,
        setStatusModalIsPublic,
        setStatusModalAttachments,
        handleRemoveModalAttachment,
        closeStatusModal,
        handleSubmitStatusModal,
    };
};